import type { CodeableConcept, Coding } from '@medplum/fhirtypes'
import { escapeRegExp, orderBy, uniqBy } from 'lodash-es'
import { NUCC_PROVIDER_TAXONOMY_SYSTEM } from '#src/codeSystems.ts'

interface NuccSpecialtiesByText {
  [specialtyText: string]: { code: string; display: string }
}

// Specialty vocabulary, grounded in the NUCC Health Care Provider Taxonomy
// (http://nucc.org/provider-taxonomy) — the US Core binding for
// PractitionerRole.specialty. Keys are the Astrana roster's specialty
// spellings; codes and displays are validated against the official NUCC v25.1
// CSV. Lookups also match the official display names, so UI-entered values
// resolve too.
//
// TODO: cut over to expanding the Medplum-hosted US Core provider-role
// ValueSet (ValueSet/$expand?url=http://hl7.org/fhir/us/core/ValueSet/
// us-core-provider-role) once its expansion is fixed upstream — it currently
// returns only 4 of the NUCC classification concepts.
// https://seenhealth.slack.com/archives/C078NUJP6B1/p1783027395703869
const SPECIALTY_TEXT_TO_NUCC: NuccSpecialtiesByText = {
  ACUPUNCTURE: { code: '171100000X', display: 'Acupuncturist' },
  'ALLERGY & IMMUNOLOGY': {
    code: '207K00000X',
    display: 'Allergy & Immunology Physician',
  },
  AMBULANCE: { code: '341600000X', display: 'Ambulance' },
  'AMBULATORY SURGICAL CENTER': {
    code: '261QA1903X',
    display: 'Ambulatory Surgical Clinic/Center',
  },
  ANESTHESIOLOGY: { code: '207L00000X', display: 'Anesthesiology Physician' },
  AUDIOLOGY: { code: '231H00000X', display: 'Audiologist' },
  // Legacy order-form specialty. NUCC has no single "behavioral health" code
  // (it's a Level-I grouping, not an assignable taxonomy code); map to the
  // closest generic individual behavioral-health provider.
  'BEHAVIORAL HEALTH': {
    code: '101YM0800X',
    display: 'Counselor, Mental Health',
  },
  // NUCC has no bariatric-surgery specialization; a bariatric surgeon is a
  // general surgeon, so map to the base Surgery classification (same code and
  // display as GENERAL SURGERY, so the shared code has one canonical display).
  'BARIATRIC SURGERY': { code: '208600000X', display: 'Surgery Physician' },
  // NUCC has no burn code; the Surgery scope folds "trauma and burns" together,
  // and burn surgeons are trauma-trained, so Trauma Surgery is the closest match.
  'BURN CARE': { code: '2086S0127X', display: 'Trauma Surgery' },
  'CARDIAC ELECTROPHYSIOLOGY': {
    code: '207RC0001X',
    display: 'Clinical Cardiac Electrophysiology Physician',
  },
  CARDIOLOGY: {
    code: '207RC0000X',
    display: 'Cardiovascular Disease Physician',
  },
  'CARDIOTHORACIC SURGERY': {
    code: '208G00000X',
    display: 'Thoracic Surgery (Cardiothoracic Vascular Surgery) Physician',
  },
  'CARDIOVASCULAR DISEASE': {
    code: '207RC0000X',
    display: 'Cardiovascular Disease Physician',
  },
  'CARDIOVASCULAR SURGERY': {
    code: '208G00000X',
    display: 'Thoracic Surgery (Cardiothoracic Vascular Surgery) Physician',
  },
  'CHILD NEUROLOGY': {
    code: '2084N0402X',
    display:
      'Neurology with Special Qualifications in Child Neurology Physician',
  },
  CHIROPRACTIC: { code: '111N00000X', display: 'Chiropractor' },
  'COLON & RECTAL SURGERY': {
    code: '208C00000X',
    display: 'Colon & Rectal Surgery Physician',
  },
  'CORNEAL OPHTHALMOLOGY': {
    code: '207WX0120X',
    display: 'Cornea and External Diseases Specialist Physician',
  },
  'CRITICAL CARE MEDICINE': {
    code: '207RC0200X',
    display: 'Critical Care Medicine (Internal Medicine) Physician',
  },
  CYTOPATHOLOGY: { code: '207ZC0500X', display: 'Cytopathology Physician' },
  // Legacy order-form specialty; maps to the base Dentistry classification.
  DENTAL: { code: '122300000X', display: 'Dentist' },
  'DENTISTRY- PROSTHONTIST': { code: '1223P0700X', display: 'Prosthodontics' },
  DERMATOLOGY: { code: '207N00000X', display: 'Dermatology Physician' },
  DIALYSIS: {
    code: '261QE0700X',
    display: 'End-Stage Renal Disease (ESRD) Treatment Clinic/Center',
  },
  DIETICIAN: { code: '133V00000X', display: 'Registered Dietitian' },
  'DURABLE MEDICAL EQUIPMENT': {
    code: '332B00000X',
    display: 'Durable Medical Equipment & Medical Supplies',
  },
  ELECTROPHYSIOLOGY: {
    code: '207RC0001X',
    display: 'Clinical Cardiac Electrophysiology Physician',
  },
  'EMERGENCY MEDICINE': {
    code: '207P00000X',
    display: 'Emergency Medicine Physician',
  },
  ENDOCRINOLOGY: {
    code: '207RE0101X',
    display: 'Endocrinology, Diabetes & Metabolism Physician',
  },
  'FAMILY PLANNING': {
    code: '261QA0005X',
    display: 'Ambulatory Family Planning Facility',
  },
  'FAMILY PRACTICE/MEDICINE': {
    code: '207Q00000X',
    display: 'Family Medicine Physician',
  },
  'FEMALE PELVIC MEDICINE & RECONSTRUCTIVE SURGERY': {
    code: '207VF0040X',
    display:
      'Urogynecology and Reconstructive Pelvic Surgery (Obstetrics & Gynecology) Physician',
  },
  'FOOT AND ANKLE SURGERY': {
    code: '213ES0103X',
    display: 'Foot & Ankle Surgery Podiatrist',
  },
  GASTROENTEROLOGY: {
    code: '207RG0100X',
    display: 'Gastroenterology Physician',
  },
  'GENERAL PRACTICE': {
    code: '208D00000X',
    display: 'General Practice Physician',
  },
  'GENERAL SURGERY': { code: '208600000X', display: 'Surgery Physician' },
  GERIATRICS: {
    code: '207RG0300X',
    display: 'Geriatric Medicine (Internal Medicine) Physician',
  },
  'GLAUCOMA OPHTHALMOLOGIST': {
    code: '207WX0009X',
    display: 'Glaucoma Specialist (Ophthalmology) Physician',
  },
  GYNECOLOGY: { code: '207VG0400X', display: 'Gynecology Physician' },
  'GYNECOLOGY ONCOLOGY': {
    code: '207VX0201X',
    display: 'Gynecologic Oncology Physician',
  },
  'HAND SURGERY': {
    code: '2086S0105X',
    display: 'Surgery of the Hand (Surgery) Physician',
  },
  'HEARING INSTRUMENT SPECIALIST': {
    code: '237700000X',
    display: 'Hearing Instrument Specialist',
  },
  'HEMATOLOGY/ONCOLOGY': {
    code: '207RH0003X',
    display: 'Hematology & Oncology Physician',
  },
  HEPATOLOGY: { code: '207RI0008X', display: 'Hepatology Physician' },
  'HOME HEALTH': { code: '251E00000X', display: 'Home Health Agency' },
  'HOME HEALTH AGENCY': { code: '251E00000X', display: 'Home Health Agency' },
  'HOME INFUSION THERAPY': {
    code: '251F00000X',
    display: 'Home Infusion Agency',
  },
  'HOSPICE & PALLIATIVE MEDICINE': {
    code: '207RH0002X',
    display: 'Hospice and Palliative Medicine (Internal Medicine) Physician',
  },
  HOSPITAL: { code: '282N00000X', display: 'General Acute Care Hospital' },
  HOSPITALIST: { code: '208M00000X', display: 'Hospitalist Physician' },
  'INFECTIOUS DISEASE': {
    code: '207RI0200X',
    display: 'Infectious Disease Physician',
  },
  INFERTILITY: {
    code: '207VE0102X',
    display: 'Reproductive Endocrinology Physician',
  },
  'INTERNAL MEDICINE': {
    code: '207R00000X',
    display: 'Internal Medicine Physician',
  },
  'INTERVENTIONAL CARDIOLOGY': {
    code: '207RI0011X',
    display: 'Interventional Cardiology Physician',
  },
  'INTERVENTIONAL RADIOLOGY': {
    code: '2085R0204X',
    display: 'Vascular & Interventional Radiology Physician',
  },
  LABORATORY: { code: '291U00000X', display: 'Clinical Medical Laboratory' },
  'MARRIAGE FAM/CHILD COUNSELOR': {
    code: '106H00000X',
    display: 'Marriage & Family Therapist',
  },
  'MATERNAL AND FETAL MEDICINE': {
    code: '207VM0101X',
    display: 'Maternal & Fetal Medicine Physician',
  },
  'MATERNAL/FETAL MEDICINE': {
    code: '207VM0101X',
    display: 'Maternal & Fetal Medicine Physician',
  },
  MIDWIFE: { code: '367A00000X', display: 'Advanced Practice Midwife' },
  'MULTI SPECIALTY': {
    code: '261QM1300X',
    display: 'Multi-Specialty Clinic/Center',
  },
  'NEONATAL-PERINATAL MEDICINE': {
    code: '2080N0001X',
    display: 'Neonatal-Perinatal Medicine Physician',
  },
  NEONATOLOGY: {
    code: '2080N0001X',
    display: 'Neonatal-Perinatal Medicine Physician',
  },
  NEPHROLOGY: { code: '207RN0300X', display: 'Nephrology Physician' },
  // "Neurointervention" is the Radiology route for Endovascular Surgical
  // Neuroradiology (the ESN specialization is also cross-listed under Neurology
  // and Neurological Surgery).
  NEUROINTERVENTION: {
    code: '2286S0064X',
    display: 'Endovascular Surgical Neuroradiology',
  },
  NEUROLOGY: { code: '2084N0400X', display: 'Neurology Physician' },
  NEUROSURGERY: {
    code: '207T00000X',
    display: 'Neurological Surgery Physician',
  },
  'NON-EM MEDICAL TRANSPORT': {
    code: '343900000X',
    display: 'Non-emergency Medical Transport (VAN)',
  },
  'NURSE PRACTITIONER': { code: '363L00000X', display: 'Nurse Practitioner' },
  NUTRITION: { code: '133V00000X', display: 'Registered Dietitian' },
  'OBSTETRICS & GYNECOLOGY': {
    code: '207V00000X',
    display: 'Obstetrics & Gynecology Physician',
  },
  'OCCUPATIONAL THERAPY': {
    code: '225X00000X',
    display: 'Occupational Therapist',
  },
  'OCULAR PLASTICS': {
    code: '207WX0200X',
    display: 'Ophthalmic Plastic and Reconstructive Surgery Physician',
  },
  ONCOLOGY: { code: '207RX0202X', display: 'Medical Oncology Physician' },
  OPHTHALMOLOGY: { code: '207W00000X', display: 'Ophthalmology Physician' },
  OPTOMETRY: { code: '152W00000X', display: 'Optometrist' },
  'ORAL & MAXILLOFACIAL SURGERY': {
    code: '204E00000X',
    display: 'Oral & Maxillofacial Surgery (D.M.D.)',
  },
  'ORTHOPEDIC SURGERY': {
    code: '207X00000X',
    display: 'Orthopaedic Surgery Physician',
  },
  OTOLARYNGOLOGY: { code: '207Y00000X', display: 'Otolaryngology Physician' },
  'PAIN MANAGEMENT': { code: '208VP0000X', display: 'Pain Medicine Physician' },
  PATHOLOGY: {
    code: '207ZP0102X',
    display: 'Anatomic Pathology & Clinical Pathology Physician',
  },
  'PEDIATRIC CARDIOLOGY': {
    code: '2080P0202X',
    display: 'Pediatric Cardiology Physician',
  },
  'PEDIATRIC ENDOCRINOLOGY': {
    code: '2080P0205X',
    display: 'Pediatric Endocrinology Physician',
  },
  'PEDIATRIC GASTROENTEROLOGY': {
    code: '2080P0206X',
    display: 'Pediatric Gastroenterology Physician',
  },
  'PEDIATRIC HEMATOLOGY-ONCOLOGY': {
    code: '2080P0207X',
    display: 'Pediatric Hematology & Oncology Physician',
  },
  'PEDIATRIC INFECTIOUS DISEASE': {
    code: '2080P0208X',
    display: 'Pediatric Infectious Diseases Physician',
  },
  'PEDIATRIC NEUROLOGY': {
    code: '2084N0402X',
    display:
      'Neurology with Special Qualifications in Child Neurology Physician',
  },
  'PEDIATRIC OPHTHALMOLOGY': {
    code: '207WX0110X',
    display: 'Pediatric Ophthalmology and Strabismus Specialist Physician',
  },
  'PEDIATRIC PULMONARY': {
    code: '2080P0214X',
    display: 'Pediatric Pulmonology Physician',
  },
  PEDIATRICS: { code: '208000000X', display: 'Pediatrics Physician' },
  PERINATOLOGY: {
    code: '207VM0101X',
    display: 'Maternal & Fetal Medicine Physician',
  },
  PHARMACY: { code: '333600000X', display: 'Pharmacy' },
  'PHYSICAL MEDICINE & REHAB': {
    code: '208100000X',
    display: 'Physical Medicine & Rehabilitation Physician',
  },
  'PHYSICAL THERAPY': { code: '225100000X', display: 'Physical Therapist' },
  'PHYSICIAN ASSISTANT': { code: '363A00000X', display: 'Physician Assistant' },
  'PHYSIOLOGICAL LAB': {
    code: '293D00000X',
    display: 'Physiological Laboratory',
  },
  'PLASTIC AND RECONSTRUCTIVE SURGERY': {
    code: '208200000X',
    display: 'Plastic Surgery Physician',
  },
  PODIATRY: { code: '213E00000X', display: 'Podiatrist' },
  'PROSTHETICS & ORTHOTICS': {
    code: '335E00000X',
    display: 'Prosthetic/Orthotic Supplier',
  },
  PSYCHIATRY: { code: '2084P0800X', display: 'Psychiatry Physician' },
  'PSYCHOLOGICAL REHABILITATION': {
    code: '103TR0400X',
    display: 'Rehabilitation Psychologist',
  },
  PSYCHOLOGY: { code: '103T00000X', display: 'Psychologist' },
  'PULMONARY DISEASES': {
    code: '207RP1001X',
    display: 'Pulmonary Disease Physician',
  },
  // Astrana authorizations send the singular spelling; alias it to the same
  // NUCC code so imported auth orders map.
  'PULMONARY DISEASE': {
    code: '207RP1001X',
    display: 'Pulmonary Disease Physician',
  },
  'RADIATION ONCOLOGY': {
    code: '2085R0001X',
    display: 'Radiation Oncology Physician',
  },
  RADIOLOGY: { code: '2085R0202X', display: 'Diagnostic Radiology Physician' },
  'REPRODUCTIVE ENDOCRINOLOGY': {
    code: '207VE0102X',
    display: 'Reproductive Endocrinology Physician',
  },
  'RETINAL-VITREOUS': {
    code: '207WX0107X',
    display: 'Retina Specialist (Ophthalmology) Physician',
  },
  RHEUMATOLOGY: { code: '207RR0500X', display: 'Rheumatology Physician' },
  'SKILLED NURSING FACILITY': {
    code: '314000000X',
    display: 'Skilled Nursing Facility',
  },
  'SKILLED NURSING PROFESSIONAL': {
    code: '163W00000X',
    display: 'Registered Nurse',
  },
  'SLEEP DISORDER': {
    code: '207RS0012X',
    display: 'Sleep Medicine (Internal Medicine) Physician',
  },
  'SLEEP MEDICINE': {
    code: '207RS0012X',
    display: 'Sleep Medicine (Internal Medicine) Physician',
  },
  'SOCIAL WORKER': { code: '104100000X', display: 'Social Worker' },
  // Legacy order-form spelling; same NUCC code as SPEECH THERAPY.
  'SPEECH PATHOLOGY': {
    code: '235Z00000X',
    display: 'Speech-Language Pathologist',
  },
  'SPEECH THERAPY': {
    code: '235Z00000X',
    display: 'Speech-Language Pathologist',
  },
  'SPORTS MEDICINE': {
    code: '207XX0005X',
    display: 'Sports Medicine (Orthopaedic Surgery) Physician',
  },
  'SURGICAL ONCOLOGY': {
    code: '2086X0206X',
    display: 'Surgical Oncology Physician',
  },
  'THORACIC SURGERY': {
    code: '208G00000X',
    display: 'Thoracic Surgery (Cardiothoracic Vascular Surgery) Physician',
  },
  'URGENT CARE': { code: '261QU0200X', display: 'Urgent Care Clinic/Center' },
  UROLOGY: { code: '208800000X', display: 'Urology Physician' },
  'VASCULAR & INTERVENTIONAL RAD': {
    code: '2085R0204X',
    display: 'Vascular & Interventional Radiology Physician',
  },
  'VASCULAR NEUROLOGY': {
    code: '2084V0102X',
    display: 'Vascular Neurology Physician',
  },
  'VASCULAR SURGERY': {
    code: '2086S0129X',
    display: 'Vascular Surgery Physician',
  },
}

// Unique canonical codings (several roster spellings share one code), sorted
// by display for pickers.
export const SPECIALTY_CODINGS: Coding[] = orderBy(
  uniqBy(Object.values(SPECIALTY_TEXT_TO_NUCC), (specialty) => specialty.code),
  (specialty) => specialty.display,
  ['asc']
).map(({ code, display }) => ({
  system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
  code,
  display,
}))

const specialtyByText = new Map<string, Coding>()
for (const [text, { code, display }] of Object.entries(
  SPECIALTY_TEXT_TO_NUCC
)) {
  specialtyByText.set(text, {
    system: NUCC_PROVIDER_TAXONOMY_SYSTEM,
    code,
    display,
  })
}
for (const coding of SPECIALTY_CODINGS) {
  specialtyByText.set((coding.display ?? '').toUpperCase(), coding)
}

const specialtyByCode = new Map<string, Coding>(
  SPECIALTY_CODINGS.map((coding) => [coding.code ?? '', coding])
)

export function nuccSpecialtyCoding(text: string): Coding | undefined {
  return specialtyByText.get(text.trim().toUpperCase())
}

// Every vocabulary specialty a free-text query names: an exact NUCC code or
// name first, else each name where one string starts a word of the other, so
// "dentistry" finds Dentist and Prosthodontics but not Otolaryngology ("ENT").
export function findSpecialtyCodings(query: string): Coding[] {
  const text = query.trim().toUpperCase()
  if (text.length < 3) {
    return []
  }
  const exact = specialtyByCode.get(text) ?? specialtyByText.get(text)
  if (exact) {
    return [exact]
  }
  const startsWord = (haystack: string, needle: string) =>
    new RegExp(`(^|[^A-Z0-9])${escapeRegExp(needle)}`).test(haystack)
  const matches = [...specialtyByText.entries()].flatMap(([name, coding]) =>
    startsWord(name, text) || startsWord(text, name) ? [coding] : []
  )
  return orderBy(
    uniqBy(matches, (coding) => coding.code),
    (coding) => coding.display,
    ['asc']
  )
}

// CodeableConcept for a specialty name: NUCC-coded when the name is known
// (original text preserved), text-only otherwise.
export function specialtyConcept(text: string): CodeableConcept {
  const coding = nuccSpecialtyCoding(text)
  return coding ? { coding: [coding], text } : { text }
}

// Stable picker value for a qualification/specialty concept: the NUCC code
// when the concept is coded (or its text resolves to one), else the raw text.
export function specialtyValue(concept: CodeableConcept): string {
  const coded = (concept.coding ?? []).find(
    (c) => c.system === NUCC_PROVIDER_TAXONOMY_SYSTEM && c.code
  )?.code
  return (
    coded ??
    nuccSpecialtyCoding(concept.text ?? '')?.code ??
    (concept.text ?? '').trim()
  )
}

// Inverse of specialtyValue: rebuild a concept from a picker value.
export function specialtyConceptForValue(value: string): CodeableConcept {
  const coding = specialtyByCode.get(value)
  return coding ? { coding: [coding], text: coding.display } : { text: value }
}

export function specialtyDisplay(value: string): string {
  return specialtyByCode.get(value)?.display ?? value
}
